import React, { ReactElement, useEffect } from 'react';
import { Button, Col, Layout, Row } from 'elmo-elements';
import { useParams } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';

import { ElearningParamType } from '@/pages/Module/routes';
import {
  useCreateQuizMutation,
  useGetQuizQuery,
  useGetThemesQuery,
  useUpdateQuizMutation,
} from '@/modules/services';
import Header from '@/pages/Module/components/Header';
import QuizSideBar from '../components/QuizSidebar';
import ScormFeedbackComponent from '../components/scormFeedback';
import ElearningFeedbackComponent from '../components/elearningFeedback';

import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';

import '../Quiz.scss';
import NoQuestion from '../components/NoQuestion';
import ContainerForm from '@/components/ContainerForm';

export interface QuizFeedbackProps {
  elearningPassAgain: string;
  elearningTryAgain: string;
  elearningFail: string;
  scormPassAgain: string;
  scormTryAgain: string;
}

const defaultValues = {
  elearningPassAgain:
    '<h2 style="color: #82a2bb; font-size:24px;">CONGRATULATIONS</h2>\r\n<p style="font-size: 16px;">You have successfully completed this quiz with a score of ::SCORE::.</p>\r\n<p style="font-size: 16px;">You can now close this window.</p>',
  elearningTryAgain:
    '<h2 style="color: #82a2bb;font-size:24px;">PLEASE TRY AGAIN </h2>\n<p style="font-size: 16px;">You were unsuccessful in completing this quiz. The pass mark for this quiz is ::PASS_MARK:: and you scored ::SCORE::. </p>\n<p style="font-size: 16px;">Click the ‘Try Again’ button below to have another attempt at the quiz.</p>',
  elearningFail:
    '<h2 style="color: #82a2bb;font-size:24px;">QUIZ FAILED</h2>\n<p style="font-size: 16px;">You were unsuccessful in completing this quiz. The pass mark for this quiz is ::PASS_MARK:: and you scored ::SCORE::.</p>',
  scormPassAgain:
    '<h2 style="color: #82a2bb;font-size:24px;">CONGRATULATIONS</h2>\n<p style="font-size: 16px;">You have successfully completed your assessment with a score of ::SCORE::.</p>',
  scormTryAgain:
    '<h2 style="color: #82a2bb;font-size:24px;">PLEASE TRY AGAIN </h2>\n<p style="font-size: 16px;">You were unsuccessful in completing this quiz. The pass mark for this quiz is ::PASS_MARK:: and you scored ::SCORE::. </p>\n<p style="font-size: 16px;">Click the ‘Try Again’ button below to have another attempt at the quiz.</p>',
};

const maxCharMessage = 'Maximum of 500 characters only';
const schema = yup.object().shape({
  elearningPassAgain: yup.string().max(500, maxCharMessage).optional(),
  elearningTryAgain: yup.string().max(500, maxCharMessage).optional(),
  elearningFail: yup.string().max(500, maxCharMessage).optional(),
  scormPassAgain: yup.string().max(500, maxCharMessage).optional(),
  scormTryAgain: yup.string().max(500, maxCharMessage).optional(),
});

const QuizFeedbackComponent = (): ReactElement => {
  const handleError = useErrorHandler();
  const dispatch = useDispatch();
  const { elearningId } = useParams<ElearningParamType>();

  const quiz = useGetQuizQuery(elearningId);
  const themes = useGetThemesQuery();

  const [updateQuiz, updateQuizStatus] = useUpdateQuizMutation();
  const [createQuiz, createQuizStatus] = useCreateQuizMutation();

  useEffect(() => {
    const isUpdateSuccess =
      updateQuizStatus.isSuccess &&
      !updateQuizStatus.isError &&
      updateQuizStatus.data;

    const isCreateSuccess =
      createQuizStatus.isSuccess &&
      !createQuizStatus.isError &&
      createQuizStatus.data;

    if (isUpdateSuccess || isCreateSuccess) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Elearning feedback Saved/ Updated Successfully',
        },
      });
    }

    if (updateQuizStatus.isError || createQuizStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [updateQuizStatus, createQuizStatus, dispatch]);

  const defaultDataValues = {
    elearningPassAgain:
      quiz.data?.lmsFeedbackMetadata.lmsContent1 ||
      defaultValues.elearningPassAgain,
    elearningTryAgain:
      quiz.data?.lmsFeedbackMetadata.lmsRetryContent ||
      defaultValues.elearningTryAgain,
    elearningFail:
      quiz.data?.lmsFeedbackMetadata.lmsFailContent ||
      defaultValues.elearningFail,
    scormPassAgain:
      quiz.data?.scormFeedbackMetadata.scormRetryContent ||
      defaultValues.scormPassAgain,
    scormTryAgain:
      quiz.data?.scormFeedbackMetadata.scormPassContent ||
      defaultValues.scormTryAgain,
  };

  const useFormMethods = useForm<QuizFeedbackProps>({
    resolver: yupResolver(schema),
    defaultValues: defaultDataValues,
  });

  useEffect(() => {
    if (quiz.data) {
      useFormMethods.reset({
        elearningPassAgain:
          quiz.data.lmsFeedbackMetadata.lmsContent1 !== undefined
            ? quiz.data.lmsFeedbackMetadata.lmsContent1
            : defaultValues.elearningPassAgain,
        elearningTryAgain:
          quiz.data.lmsFeedbackMetadata.lmsRetryContent !== undefined
            ? quiz.data.lmsFeedbackMetadata.lmsRetryContent
            : defaultValues.elearningTryAgain,
        elearningFail:
          quiz.data.lmsFeedbackMetadata.lmsFailContent !== undefined
            ? quiz.data.lmsFeedbackMetadata.lmsFailContent
            : defaultValues.elearningFail,
        scormTryAgain:
          quiz.data.scormFeedbackMetadata.scormPassContent !== undefined
            ? quiz.data.scormFeedbackMetadata.scormPassContent
            : defaultValues.scormPassAgain,
        scormPassAgain:
          quiz.data.scormFeedbackMetadata.scormRetryContent !== undefined
            ? quiz.data.scormFeedbackMetadata.scormRetryContent
            : defaultValues.scormTryAgain,
      });
    }
  }, [quiz.data, quiz.isLoading, useFormMethods]);

  const onSubmit = (data: QuizFeedbackProps) => {
    if (quiz.data) {
      const submitData = {
        ...quiz.data,

        lmsFeedbackMetadata: {
          ...quiz.data.lmsFeedbackMetadata,
          lmsContent1: data.elearningPassAgain,
          lmsRetryContent: data.elearningTryAgain,
          lmsFailContent: data.elearningFail,
        },
        scormFeedbackMetadata: {
          ...quiz.data.scormFeedbackMetadata,
          scormRetryContent: data.scormTryAgain,
          scormPassContent: data.scormPassAgain,
        },
      };

      updateQuiz(submitData);
    }
  };

  if (quiz.isError) {
    handleError(quiz.error);
  }

  if (themes.isError) {
    handleError(themes.error);
  }

  if (!quiz.data) {
    return <div className="isLoading" />;
  }

  return (
    <>
      <Layout.Header>
        <Header />
      </Layout.Header>
      <div className="editor-layout__body">
        <QuizSideBar />
        <Layout.Content>
          <div className="center-content">
            {!quiz.data && !quiz.isLoading ? (
              <NoQuestion />
            ) : (
              <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
                <ContainerForm maxColumns={2}>
                  <FormProvider {...useFormMethods}>
                    <ElearningFeedbackComponent />
                    {/* TODO: uncomment this when Scrom & Breathe is implemented */}
                    {/* <ScormFeedbackComponent /> */}
                  </FormProvider>
                </ContainerForm>
                <Row className="mt-4">
                  <Col xs={8} role="presentation">
                    <Button
                      type="primary"
                      htmlType="submit"
                      ariaLabel="Submit Form"
                      isLoading={
                        createQuizStatus.isLoading || updateQuizStatus.isLoading
                      }
                      isUppercase={false}
                      className="px-5"
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </div>
        </Layout.Content>
      </div>
    </>
  );
};

export default QuizFeedbackComponent;
